import React, { useState, useCallback } from "react"

import { formatCurrency } from "utils/currency"
import useAddToCart from "utils/useAddToCart"

import {
  AddToCart,
  AddToCartButton,
  CartOptions,
  DeliveryInterval,
  DeliveryIntervalSpan,
  Notes,
  PaymentType,
  PaymentTypeName,
  PaymentTypePrice,
  PaymentTypes,
  ProductServings,
  PurchaseForm,
  QuantitySelect,
  QuantitySelectDownArrow,
  QuantitySelectWrapper,
  Tooltip,
  TooltipBody,
  TooltipContainer,
  TooltipIconContainer,
  TooltipTitle,
  TooltipTopArrow,
} from "./styles"

const SupplementPurchaseForm = ({ variants, dataId, ...props }) => {
  const variant = variants && variants.length > 0 ? variants[0] : null
  const product = variant?.product

  const addToCartRequest = useAddToCart()

  const subscriptionDisabled = product ? product?.metaDisableSubscriptions?.value : null
  const sellingPlanId =
    !subscriptionDisabled && variant
      ? variant.skioSellingPlanGroupResource?.sellingPlan?.sellingPlanResourceId
      : null

  const [purchaseData, setPurchaseData] = useState({
    selectedQuantity: 1,
    paymentType: !subscriptionDisabled ? "recurring" : "alacarte",
    price: getPrice(!subscriptionDisabled ? "recurring" : "alacarte", variant) || 0.0,
  })

  const handleSelectPaymentType = (paymentType) => {
    setPurchaseData({
      ...purchaseData,
      paymentType,
      price: getPrice(paymentType, variant) || 0.0,
    })
  }

  // Add product(s) to the cart
  const handleAddToCart = useCallback(() => {
    if (addToCartRequest.addingToCart) return

    addToCartRequest.addProductToCart(
      variant.variantId,
      purchaseData.price,
      purchaseData.selectedQuantity,
      variant.title,
      true,
      false,
      false,
      sellingPlanId,
    )
  }, [addToCartRequest, purchaseData, variant, sellingPlanId])

  const sellingPlan = variant.skioSellingPlanGroupResource?.sellingPlan
    ? variant.skioSellingPlanGroupResource.sellingPlan
    : null

  return (
    <PurchaseForm data-exp-id={dataId} {...props}>
      {product && (
        <>
          {!subscriptionDisabled && (
            <PaymentTypes>
              <PaymentType
                active={purchaseData.paymentType === "recurring"}
                aria-label="Select Subscribe and Save Option"
                role="button"
                tabIndex="0"
                onClick={() => handleSelectPaymentType("recurring")}
              >
                <PaymentTypeName>Subscribe And Save</PaymentTypeName>
                <PaymentTypePrice id="current-subscription-price">
                  {product?.metaSubscriptionPrice?.value &&
                    formatCurrency(product.metaSubscriptionPrice.value / 100, true)}
                </PaymentTypePrice>
              </PaymentType>
              <PaymentType
                active={purchaseData.paymentType === "alacarte"}
                aria-label="Select Buy Once Option"
                role="button"
                tabIndex="0"
                onClick={() => handleSelectPaymentType("alacarte")}
              >
                <PaymentTypeName>Buy Once</PaymentTypeName>
                <PaymentTypePrice>{formatCurrency(variant.price, true)}</PaymentTypePrice>
              </PaymentType>
            </PaymentTypes>
          )}
          <CartOptions subscriptionDisabled={subscriptionDisabled}>
            <DeliveryInterval>
              <DeliveryIntervalSpan>Send me</DeliveryIntervalSpan>
              <QuantitySelectWrapper>
                <QuantitySelect
                  value={purchaseData.selectedQuantity}
                  onChange={(e) =>
                    setPurchaseData({ ...purchaseData, selectedQuantity: parseInt(e.target.value) })
                  }
                  type="number"
                  min="1"
                  aria-label="Select Product Quantity"
                >
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5">5</option>
                  <option value="6">6</option>
                  <option value="7">7</option>
                  <option value="8">8</option>
                  <option value="9">9</option>
                  <option value="10">10</option>
                </QuantitySelect>
                <QuantitySelectDownArrow id="arrow-down-icon" width="7px" height="9px" />
              </QuantitySelectWrapper>
              <DeliveryIntervalSpan>{product.metaContainerType?.value}(s)</DeliveryIntervalSpan>
              {!subscriptionDisabled && purchaseData.paymentType === "recurring" && (
                <DeliveryIntervalSpan className="show-subscription">
                  every {formatDeliveryPolicy(sellingPlan?.intervalCount, sellingPlan?.interval)}
                </DeliveryIntervalSpan>
              )}
              <ProductServings>
                {product.metaUnitNumber?.value} {product.metaUnitType?.value} per{" "}
                {product.metaContainerType?.value}
              </ProductServings>
            </DeliveryInterval>
          </CartOptions>
          <AddToCart>
            <AddToCartButton
              onClick={handleAddToCart}
              type="button"
              notEnabled={addToCartRequest.addingToCart}
            >
              {!addToCartRequest.addingToCart && (
                <>
                  {formatCurrency(purchaseData.price * purchaseData.selectedQuantity, true)}
                  <span>&nbsp;| Add to Cart</span>
                </>
              )}
              {addToCartRequest.addingToCart && <span>Adding to cart...</span>}
            </AddToCartButton>
          </AddToCart>
          <Notes>
            Free shipping in the US •&nbsp;
            <Tooltip>
              <TooltipIconContainer>
                <TooltipTopArrow />
                <span>Refund &amp; Cancellation Policy</span>
              </TooltipIconContainer>
              <TooltipContainer type="refund">
                <TooltipTitle>Refund Policy</TooltipTitle>
                <TooltipBody>
                  All purchases, including 6 month and 12 month prepay, are considered final sale
                  and are not refundable.
                  <TooltipTitle>Cancellation Policy</TooltipTitle>
                  <TooltipBody>
                    Cancel your monthly subscription, or stop 6 and 12 month prepay from auto
                    renewing for a new term by emailing or calling us at any time. For more
                    information, read our Terms and Conditions.
                  </TooltipBody>
                </TooltipBody>
              </TooltipContainer>
            </Tooltip>
            &nbsp;•&nbsp;
            <Tooltip>
              <TooltipIconContainer>
                <TooltipTopArrow />
                <span>Shipping Policy</span>
              </TooltipIconContainer>
              <TooltipContainer type="shipping">
                <TooltipTitle>Shipping Policy</TooltipTitle>
                <TooltipBody>
                  <ul>
                    <li>
                      <span>Free shipping within the US, 3-5 business days</span>
                    </li>
                    <li>
                      <span>
                        International shipping to Canada, cost dependent on location and package
                        weight
                      </span>
                    </li>
                  </ul>
                </TooltipBody>
              </TooltipContainer>
            </Tooltip>
          </Notes>
        </>
      )}
    </PurchaseForm>
  )
}

const getPrice = (paymentType, variant) =>
  paymentType === "alacarte"
    ? parseFloat(variant.price)
    : parseFloat(variant.product?.metaSubscriptionPrice?.value) / 100

const formatDeliveryPolicy = (intervalCount, interval) => {
  let formattedString = ""
  switch (interval.toUpperCase()) {
    case "DAY":
      formattedString = `${intervalCount} day${intervalCount > 1 ? "s" : ""}`
      break
    case "WEEK":
      formattedString = `${intervalCount} week${intervalCount > 1 ? "s" : ""}`
      break
    case "MONTH":
      formattedString = `${intervalCount} month${intervalCount > 1 ? "s" : ""}`
      break
    case "YEAR":
      formattedString = `${intervalCount} year${intervalCount > 1 ? "s" : ""}`
      break
    default:
      formattedString = ""
  }

  return formattedString
}

export default SupplementPurchaseForm
